import React, { useState } from 'react'

import type { TChatSummaryDto } from '@homepal/api-contract'

import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Divider from 'components_new/atoms/Divider'
import Icon from 'components_new/atoms/Icon'
import Skeleton from 'components_new/atoms/Skeleton'
import Text from 'components_new/atoms/Text'

import SearchField from 'components_new/molecules/SearchField'

import ChatHistoryRow from './ChatHistoryRow'
import { ChatDay, groupChatsByDay } from './historyLabels'

type GroupLabelKey = 'history_today' | 'history_yesterday'
  | 'history_earlier'

const GROUP_LABEL: Record<ChatDay, GroupLabelKey> = {
  today: 'history_today',
  yesterday: 'history_yesterday',
  earlier: 'history_earlier'
}

interface ChatHistoryListProps {
  chats: TChatSummaryDto[]
  loading: boolean
  onBack: () => void
  onSelect: (chatId: string) => void
}

const ChatHistoryList = (props: ChatHistoryListProps) => {
  const { chats, loading, onBack, onSelect } = props

  const { t } = useTranslation()

  const [search, setSearch] = useState('')

  // A refresh keeps the rows in place instead of blanking them.
  const showSkeletons = loading && chats.length === 0

  const query = search.trim().toLowerCase()
  const matches = query
    ? chats.filter((chat) => chat.title.toLowerCase().includes(query))
    : chats
  const groups = groupChatsByDay(matches)

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0
      }}
    >
      <Box sx={{ px: 2, pt: 1 }}>
        <Text
          color="text.secondary"
          onClick={onBack}
          variant="body2"
          sx={{
            alignItems: 'center',
            cursor: 'pointer',
            display: 'inline-flex',
            gap: 1,
            '&:hover': { color: 'text.primary' }
          }}
        >
          <Icon fontSize="small" name="ArrowBack" />
          {t('global', 'back')}
        </Text>

        <Text color="text.primary" variant="h5" sx={{ mt: 1 }}>
          {t('chat', 'history_title')}
        </Text>

        <Text color="text.secondary" variant="body2" sx={{ mb: 2, mt: 0.5 }}>
          {t('chat', 'history_scope_note')}
        </Text>

        <SearchField
          onClear={() => setSearch('')}
          onSearch={setSearch}
          placeholder={t('chat', 'history_search_placeholder')}
          size="small"
          sx={{ width: '100%' }}
          value={search}
        />
      </Box>

      <Divider sx={{ mt: 2 }} />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', py: 1 }}>
        {showSkeletons ? (
          <Box sx={{ px: 2 }}>
            <Skeleton height={56} />
            <Skeleton height={56} />
            <Skeleton height={56} />
          </Box>
        ) : null}

        {!showSkeletons && groups.length === 0 ? (
          <Text
            align="center"
            color="text.secondary"
            variant="body2"
            sx={{ px: 2, py: 4 }}
          >
            {query
              ? t('chat', 'history_no_matches')
              : t('chat', 'history_empty')}
          </Text>
        ) : null}

        {groups.map((group) => (
          <Box key={group.day} sx={{ mb: 1 }}>
            <Text
              color="text.secondary"
              variant="overline"
              sx={{ display: 'block', px: 2, py: 1 }}
            >
              {t('chat', GROUP_LABEL[group.day])}
            </Text>

            {group.chats.map((chat) => (
              <ChatHistoryRow
                chat={chat}
                key={chat.id}
                onSelect={() => onSelect(chat.id)}
                search={query}
              />
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export default ChatHistoryList
